import {useState, useMemo, useEffect, useRef} from 'react';
import { createPortal } from "react-dom";
import FocusTrap from "focus-trap-react";
import { useDispatch, useSelector } from "react-redux";
import { FaTimes } from "react-icons/fa";
// import SearchBox from '../../../common/SearchBox/SearchBox';
import {
  fetchVectorIdentities,
  clearVectorIdentities,
} from "../../../../store/slices/dashboardSlice";
import "../../../modules/identities/MfaUsersModal/MfaUsersModal.css";
import { Loading, Pagination, SearchBox, Badge } from "../../../common";
import { getInitials } from "../../../../utils/helpers";
import "../../identities/IdentityTable/IdentityTable.css";
import { TYPE_COLORS } from "../../../../utils/constants";
import { useTheme } from "../../../../context/ThemeContext";


const AttackVectorModal = ({
  isOpen,
  onClose,
  vectorData, // Contains vector_id and attack_vector_name
  activeTab, // 'human' or 'non-human'
  closeOnOverlay = true,
  closeOnEscape = true,
}) => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const modalRef = useRef(null);
  const { isDark } = useTheme();

  // console.log("vector_data",vectorData);
  // 1. Pull data and loading state from Redux
  const {
    data: identities,
    totalCount,
    loading,
    error,
  } = useSelector((state) => state.dashboard.vectorIdentities);

  useEffect(() => {
    if (isOpen && vectorData?.vector_id) {
      dispatch(
        fetchVectorIdentities({
          vectorId: vectorData.vector_id,
          page: currentPage,
          limit: itemsPerPage,
          search: searchValue,
        }),
      );
    }
  }, [isOpen, vectorData?.vector_id, currentPage, searchValue, dispatch]);

  // useEffect(() => {
  //   return () => {
  //     if (isOpen) {
  //       dispatch(clearVectorIdentities());
  //       setSearchValue("");
  //     }
  //   };
  // }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen) setCurrentPage(1);
  }, [isOpen, vectorData?.vector_id]);

  useEffect(() => {
    return () => {
      if (isOpen) {
        dispatch(clearVectorIdentities());
        setSearchValue("");
      }
    };
  }, [isOpen, dispatch]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);


  useEffect(() => {
    if (!isOpen) return;

    const appContent = document.getElementById("app-content");
    const previousOverflow = document.body.style.overflow;
    const previousFocusedElement = document.activeElement;

    /**
     * Disable background interaction and keyboard tab focus.
     * Important: modal must be rendered outside #app-content using portal.
     */
    appContent?.setAttribute("inert", "");
    appContent?.setAttribute("aria-hidden", "true");

    document.body.style.overflow = "hidden";
    // 3. Handle escape key & body scroll lock
    const handleEscape = (e) => {
      if (closeOnEscape && e.key === "Escape" && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      appContent?.removeAttribute("inert");
      appContent?.removeAttribute("aria-hidden");

      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, closeOnEscape, onClose]);

  // 4. Filter identities based on search
  const filteredIdentities = useMemo(() => {
    if (!identities) return [];
    if (!searchValue.trim()) return identities;

    const searchLower = searchValue.toLowerCase();
    return identities.filter((id) => {
      const name = (id.display_name || id.name || "").toLowerCase();
      const identifier = (id.upn || id.id || "").toLowerCase();
      return name.includes(searchLower) || identifier.includes(searchLower);
    });
  }, [identities, searchValue]);

  if (!isOpen || !vectorData) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div className={`modal-overlay ${isDark ? 'dark dark-theme' : 'light light-theme'}`} onClick={handleOverlayClick}>
      <FocusTrap
        active={isOpen}
        focusTrapOptions={{
          initialFocus: () =>
            modalRef.current?.querySelector(".modal-close") || modalRef.current,
          fallbackFocus: () => modalRef.current,
          returnFocusOnDeactivate: true,
          escapeDeactivates: false,
          clickOutsideDeactivates: false,
          allowOutsideClick: true,
        }}
      >
        <div ref={modalRef} className="modal modal-lg" role="dialog" aria-modal="true" tabIndex={-1}
          onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header mfa-modal-header">
            <div className="mfa-modal-header-content">
              <h2 className="modal-title">
                {vectorData?.attack_vector_name?.replace(/_/g, " ")}
              </h2>
              <div className="mfa-modal-meta">
                <span className="mfa-modal-count">
                  <strong>{totalCount}</strong> {activeTab.replace("-", " ")}{" "}
                  identities
                </span>
              </div>
            </div>

            <div className="mfa-header-search-wrapper">
              <SearchBox
                value={searchValue}
                onChange={setSearchValue}
                placeholder="Search by name or ID..."
                debounceMs={500}
                showClear={true}
                className="mfa-header-search"
              />
            </div>

            <button
              className="modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body">
            {loading ? (
              <Loading text="loading identities" />
            ) : error ? (
              <div className="mfa-modal-error">
                <p>Error loading identities: {error.message || error}</p>
              </div>
            ) : filteredIdentities.length > 0 ? (
              <>
                <div
                  className="identity-table-container"
                  role="region"
                  aria-label="MFA Identities table"
                >
                  <table className="identity-table" role="table">
                    <thead>
                      <tr>
                        <th scope="col" className="th-identity">
                          Identity
                        </th>
                        <th
                          scope="col"
                          className={`th-${activeTab === "human" ? "department" : "type"}`}
                        >
                          {activeTab === "human" ? "Department" : "Type"}
                        </th>
                        {/* Optional: Add an empty header for actions if you add a view button later */}
                        {/* <th scope="col" className="th-actions">Actions</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredIdentities.map((id, idx) => {
                        const rowId = id.id || id.objectId || idx;
                        const displayName =
                          id.display_name || id.name || "Unnamed";
                        const secondaryInfo = id.upn || id.principal_id || "N/A";
                        const typeOrDept = id.department || id.type || "N/A";

                        return (
                          <tr key={rowId} className="identity-row" role="row">
                            {/* ── Identity Column (Avatar + Info) ── */}
                            <td className="td-identity">
                              <div className="identity-cell">
                                <div
                                  className="identity-avatar"
                                  style={{
                                    backgroundColor: `${TYPE_COLORS[id?.type] || "#999"}15`,
                                    color: TYPE_COLORS[id?.type] || "#999",
                                  }}
                                  aria-hidden="true"
                                >
                                  {getInitials(displayName)}
                                </div>

                                <div className="identity-info">
                                  <div className="identity-name">
                                    {displayName}
                                  </div>
                                  <div className="identity-secondary">
                                    {secondaryInfo}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* ── Contextual Column (Dept or Type) ── */}
                            <td
                              className={
                                activeTab === "human"
                                  ? "td-department"
                                  : "td-type"
                              }
                            >
                              {activeTab === "human" ? (
                                <span className="cell-text">{typeOrDept}</span>
                              ) : (
                                <Badge>{typeOrDept}</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!searchValue && (
                  <div className="modal-pagination-wrapper">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalCount}
                      itemsPerPage={itemsPerPage}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="usecase-modal-empty">
                <p>
                  {searchValue
                    ? `No results for "${searchValue}"`
                    : "No identities found."}
                </p>
              </div>
            )}
          </div>
        </div>
      </FocusTrap>
    </div>,
    document.body
  );
};

export default AttackVectorModal;
